import { useCallback, useEffect, useState } from 'react';
import type { CreateProjectInput, Project, UpdateProjectInput } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

/**
 * Charge et gère les projets **transverses** de l'organisation (ADR-0024). `refresh` est rejoué après
 * chaque écriture pour tenir les compteurs à jour sans recharger la page. Les écritures propagent
 * leur erreur (un rôle non-éditeur reçoit 403) ; la lecture, elle, tombe en liste vide sans bloquer.
 */
export function useProjects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setProjects(await apiFetch<Project[]>('/projects'));
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const create = useCallback(
    async (input: CreateProjectInput) => {
      // On rend le projet créé : le flux « Démarrer un objectif » enchaîne aussitôt vers son détail.
      const created = await apiFetch<Project>('/projects', { method: 'POST', body: input });
      await refresh();
      return created;
    },
    [refresh],
  );

  const rename = useCallback(
    async (id: string, input: UpdateProjectInput) => {
      await apiFetch(`/projects/${id}`, { method: 'PATCH', body: input });
      await refresh();
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await apiFetch(`/projects/${id}`, { method: 'DELETE' });
      await refresh();
    },
    [refresh],
  );

  return { projects, loading, create, rename, remove, refresh };
}
